'use client'

import * as React from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import { eachMonthOfInterval, format, startOfMonth } from 'date-fns'
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectValue,
  SelectItem,
} from '~/sotano-kit/primitives/select'
import { useQuery } from '@tanstack/react-query'
import {
  BlockWithChildren,
  RangeEnum,
  TimeSeriesRange,
  isInstanceBlock,
  type EventBlock,
} from '@basehub/data/replicache/index'
import { Portal } from '~/components/portal'
import { AxisInterval } from 'recharts/types/util/types'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useUserStore } from '~/app/_store/user-store'
import { EventSchemaHelpers } from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'

const defaultLabels = {
  [RangeEnum.day]: 'Last 24 hours',
  [RangeEnum.week]: 'Last 7 days',
  [RangeEnum.month]: 'Last month',
  [RangeEnum.year]: 'Last year',
  [RangeEnum.allTime]: 'All time',
}

export const getEventChartQueryKey = ({
  blockId,
  teamId,
  repoId,
  range,
}: {
  blockId: string
  teamId?: string
  repoId?: string
  range: TimeSeriesRange
}) => {
  return ['event-block', 'time-series', blockId, teamId, repoId, range]
}

export const TimeSeries = ({
  block,
  parentBlock,
}: {
  block: EventBlock
  parentBlock: BlockWithChildren
}) => {
  const { replicache, team, repoId } = useRepoReplicache().data ?? {}
  const userId = useUserStore((s) => s.optimisticUser?.id)
  const containerId = React.useId()

  const blockIdInTemplate: string = React.useMemo(() => {
    if (!isInstanceBlock(parentBlock)) return block.id
    invariant(
      block.instanceChildTarget,
      'Expected instanceChildTarget to be defined.'
    )

    return block.instanceChildTarget
  }, [block.id, block.instanceChildTarget, parentBlock])

  const range = useUserPreferences(
    (s) => s.eventSelectedRange[blockIdInTemplate] ?? RangeEnum.month
  )

  const { data: queryData } = useQuery(
    getEventChartQueryKey({
      blockId: idToSourceBlockId(block),
      teamId: team?.id,
      repoId,
      range,
    }),
    async ({ queryKey }) => {
      const res = await fetch(
        `/api/v2/events/query?block-id=${queryKey[2]}&team-id=${queryKey[3]}&repo-id=${queryKey[4]}&range=${queryKey[5]}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      const data = EventSchemaHelpers.eventsResponseSchema.parse(
        await res.json()
      )
      if (!data.success) {
        console.error(data.error)
        return null
      }

      return {
        events: data.data,
        resolvedRange: queryKey[4] as TimeSeriesRange,
      }
    },
    {
      enabled: Boolean(team?.id && repoId),
      refetchInterval: 3 * 1000,
      keepPreviousData: true,
    }
  )

  const groups = React.useMemo(
    () => queryData?.events ?? [],
    [queryData?.events]
  )

  const idleResult: EventSchemaHelpers.EventNode = React.useMemo(() => {
    return {
      count: groups.reduce((acc, group) => {
        acc += group.count
        return acc
      }, 0),
      label: defaultLabels[range],
    }
  }, [groups, range])

  const formatDate = React.useCallback(
    (label: string, options?: Partial<{ type: 'full' | 'short' }>) => {
      const date = new Date(label.replace('Z', ''))
      if (isNaN(date.getTime())) {
        return label
      }

      switch (range) {
        case RangeEnum.day:
          // Show hour only
          return new Date(label).toLocaleTimeString('en-US', {
            hour: 'numeric',
            hour12: true,
          })
        case RangeEnum.week:
        case RangeEnum.month:
          // May 3rd -- format
          return options?.type === 'full'
            ? format(date, 'dd LLL, yyyy')
            : format(date, 'dd LLL')
        default:
          return options?.type === 'full'
            ? format(date, 'dd LLL, yyyy')
            : format(date, 'LLL yy')
      }
    },
    [range]
  )

  const { allBreakpoints: data } = React.useMemo(
    () => EventSchemaHelpers.fillTimePeriod(groups, range),
    [groups, range]
  )

  const tooltipContent = React.useCallback(
    ({
      active,
      payload,
      label,
    }: {
      active?: boolean
      payload?: { value?: number }[]
      label?: string
    }) => {
      let count = 0
      let resultLabel = ''
      if (!active) {
        count = idleResult.count
        resultLabel = idleResult.label
      }

      if (payload && typeof payload[0]?.value === 'number' && label) {
        count = payload[0].value
        resultLabel = label
      }

      return (
        <>
          <Portal id={containerId}>
            <span className="text-dark-bright text-2xl leading-[1.1] row-start-1 col-start-1 ml-2">
              {count}{' '}
              <span className="text-dark-muted font-medium text-sm tracking-normal">
                Count
              </span>
            </span>
          </Portal>
          <div className="bg-dark-shade border border-dark-border-solid rounded-md px-2 py-1 text-dark-muted text-xs font-medium leading-normal">
            {formatDate(resultLabel, { type: 'full' })}
          </div>
        </>
      )
    },
    [containerId, formatDate, idleResult.count, idleResult.label]
  )

  const xAxisProps = React.useMemo<{
    interval?: AxisInterval
    ticks?: Array<string>
  }>(() => {
    switch (range) {
      case 'day':
        return { interval: 1 }
      case 'week':
        return { interval: 0 }
      case 'month':
        return { interval: 2 }
      case 'year':
      case 'allTime':
        return {
          interval: 'preserveStart',
          ticks: eachMonthOfInterval({
            start: startOfMonth(new Date(data[0]!.label)),
            end: new Date(data.at(-1)!.label),
          })
            .map((date) => date.toISOString().replace(/T.*/, 'T00:00:00.000Z'))
            .concat(data.at(-1)!.label),
        }
      default:
        return { interval: 'preserveStart' }
    }
  }, [data, range])

  const handleRangeChange = React.useCallback(
    async (v: string) => {
      if (!userId || !replicache?.mutate) return

      await replicache.mutate.setSelectedEventRange({
        userId,
        eventBlockId: blockIdInTemplate,
        rangeValue: v as TimeSeriesRange,
      })
    },
    [blockIdInTemplate, replicache?.mutate, userId]
  )

  return (
    <div
      className="grid grid-cols-[1fr_auto] grid-rows-[auto_1fr] items-center gap-y-5 gap-x-2 col-span-full"
      id={containerId}
    >
      <Select onValueChange={handleRangeChange} value={range}>
        <SelectTrigger className="col-start-2">
          <SelectValue placeholder="Range" defaultValue="week" />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(defaultLabels).map(([key, label]) => (
            <SelectItem key={key} value={key}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <figure className="w-full col-span-full h-72 relative row-start-[-2]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 0, right: 52, bottom: 0, left: -24 }}
            className="[&>svg]:overflow-visible"
          >
            <defs>
              <linearGradient
                id="colorValue"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
                className="text-dark-accent"
              >
                <stop offset="10%" stopColor="currentColor" stopOpacity={1} />
                <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              vertical={false}
              className="stroke-dark-border-solid first-of-type:opacity-0"
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="label"
              textAnchor="right"
              className="[&_tspan]:text-dark-faint [&_tspan]:text-xs [&_line]:stroke-dark-border-solid"
              tickLine={false}
              tickFormatter={(t) => formatDate(t)}
              {...xAxisProps}
            />
            <YAxis
              className="[&_tspan]:text-dark-faint [&_tspan]:text-xs [&_tspan]:tabular-nums [&_line]:stroke-dark-border-solid"
              interval={0}
              allowDecimals={false}
              tickCount={8}
              tickLine={false}
              domain={[
                0,
                (dataMax: number) =>
                  !dataMax ? 100 : Math.ceil(dataMax / 7) * 7,
              ]}
            />
            <Tooltip
              content={tooltipContent}
              cursor={{ className: 'stroke-dark-accent' }}
              animationDuration={100}
              animationEasing="ease-in-out"
            />
            <Area
              isAnimationActive={false}
              type="linear"
              dataKey="count"
              className="stroke-dark-accent"
              stroke="inherit"
              strokeWidth={2}
              fill="url(#colorValue)"
              dot={{ display: 'none' }}
              activeDot={{
                r: 4.6,
                fill: '#ff7300',
                strokeWidth: 2,
                className: 'fill-dark-accent stroke-dark-base',
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </figure>
    </div>
  )
}
